import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Card, SnackBarProvider } from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import { shouldRenderCard } from '~/entities/evaluations/utils/shouldRenderCard';
import TransactionHistory from '~/entities/evaluations/transaction/history/TransactionHistory';

import { DEFAULT_COLUMNS } from '~/entities/evaluations/transaction/history/constants';

const CollapsiboxWrapper = styled.div`
  display: grid;
  /* On the entity profile the card renders flush against the tab bar, so give it some breathing room. */
  ${props => (props.$view === 'entity' ? 'margin-top: var(--cl-space-5);' : '')}
`;
const CollapsiboxTransactionHistory = styled(Card)`
  overflow: auto;
`;

const TransactionHistoryCollapsiboxPropTypes = {
  agent: PropTypes.object,
  entity: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  header: PropTypes.string,
  view: PropTypes.string,
  canSendWebhookFromPage: PropTypes.bool,
  caseToken: PropTypes.string,
  canUpdateStatus: PropTypes.bool,
};
const TransactionHistoryCollapsiboxDefaultTypes = {
  agent: null,
  header: 'Recent Transactions',
  loading: false,
  view: null,
  canSendWebhookFromPage: false,
  canUpdateStatus: false,
  caseToken: null,
};

const TransactionHistoryCollapsibox = ({
  loading,
  header,
  agent,
  entity,
  view,
  canSendWebhookFromPage,
  canUpdateStatus,
  caseToken,
}) => {
  const [inPrintMode, setInPrintMode] = useState(false);
  const [hasTransactions, setHasTransactions] = useState(true);
  const { featureFlags } = useContext(UserContext);
  const hideEmptyCardsPesEnabled = Boolean(featureFlags?.removeEmptyCardsPes);

  // The evaluation link column is scoped to the entity profile only (view === 'entity').
  // It is inserted directly after the "Transaction ID" column per its column ordering.
  const buildPriorityColumns = () => {
    if (view !== 'entity') {
      return [...DEFAULT_COLUMNS.priority];
    }

    // An absent "Transaction ID" yields -1, placing the column first — which is what we want
    // anyway, since the table's default column set hides "Transaction ID".
    const insertIndex = DEFAULT_COLUMNS.priority.indexOf('Transaction ID') + 1;

    return [
      ...DEFAULT_COLUMNS.priority.slice(0, insertIndex),
      'Evaluation',
      ...DEFAULT_COLUMNS.priority.slice(insertIndex),
    ];
  };

  const transactionColumns = {
    priority_columns: buildPriorityColumns(),
    secondary_columns: [...DEFAULT_COLUMNS.secondary],
  };

  useEffect(() => {
    const handlerBeforePrint = () => setInPrintMode(true);
    const handlerAfterPrint = () => setInPrintMode(false);
    window.addEventListener('beforeprint', handlerBeforePrint);
    window.addEventListener('afterprint', handlerAfterPrint);
  }, []);

  const handleTransactionsLoad = useCallback(loaded => {
    setHasTransactions(loaded);
  }, []);

  if (!shouldRenderCard(hasTransactions, hideEmptyCardsPesEnabled)) {
    return null;
  }

  return (
    <CollapsiboxWrapper $view={view}>
      <CollapsiboxTransactionHistory
        header={header}
        {...(inPrintMode ? { collapsed: false } : {})}
      >
        <SnackBarProvider>
          <TransactionHistory
            hasEvidenceModal
            transactionColumns={transactionColumns}
            loading={loading}
            agent={agent}
            entity={entity}
            view={view}
            isActionable
            canSendWebhookFromPage={canSendWebhookFromPage}
            canUpdateStatus={canUpdateStatus}
            caseToken={caseToken}
            onLoad={handleTransactionsLoad}
          />
        </SnackBarProvider>
      </CollapsiboxTransactionHistory>
    </CollapsiboxWrapper>
  );
};

TransactionHistoryCollapsibox.propTypes = TransactionHistoryCollapsiboxPropTypes;
TransactionHistoryCollapsibox.defaultProps = TransactionHistoryCollapsiboxDefaultTypes;

export default TransactionHistoryCollapsibox;
